import { Box, Stack, Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors, shadows } from "./services.styles";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import PercentIcon from "@mui/icons-material/Percent";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

const MetricCard = ({
  icon,
  iconBg,
  title,
  value,
  subtitle,
  subtitleColor,
  trend,
  trendColor,
}) => {
  const theme = useTheme();
  const colors = getColors(theme);

  const formatValue = (val) => {
    if (typeof val === "number") {
      if (val >= 100000) {
        return `₹${(val / 100000).toFixed(1)}L`;
      }
      return `₹${val.toLocaleString()}`;
    }
    return val;
  };

  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 1.5,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
        transition: "all 0.2s ease",
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          boxShadow: shadows.cardHover,
          transform: "translateY(-2px)",
        },
      }}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.5,
            color: colors.textMuted,
            textTransform: "uppercase",
            mb: 1.5,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
      </Stack>

      <Typography
        sx={{
          fontSize: 20,
          fontWeight: 700,
          color: colors.textPrimary,
          mb: 1,
          lineHeight: 1.2,
        }}
      >
        {formatValue(value)}
      </Typography>

      {subtitle && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {trend && (
            <TrendingUpIcon
              sx={{ fontSize: 14, color: trendColor || colors.success }}
            />
          )}
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: subtitleColor || colors.textSecondary,
            }}
          >
            {subtitle}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const ServiceMetricsCards = ({ data = {} }) => {
  const theme = useTheme();
  const colors = getColors(theme);

  // Derive win ratio from totalWon / totalLeads
  const totalLeads = data?.totalLeads || 0;
  const totalWon = data?.totalWon || 0;
  const winRatio = totalLeads > 0
    ? Math.round((totalWon / totalLeads) * 1000) / 10
    : 0;

  // Build subtitle for highest revenue — show lead count for context
  const revenueSubtitle = data?.highestRevenue?.name
    ? data.highestRevenue.totalLeads
      ? `${data.highestRevenue.name} · ${data.highestRevenue.totalLeads} leads`
      : data.highestRevenue.name
    : "No data";

  const metrics = [
    {
      icon: <EmojiEventsOutlinedIcon sx={{ fontSize: 18, color: "#F59E0B" }} />,
      iconBg: "#FEF3C7",
      title: "HIGHEST REVENUE",
      value: data?.highestRevenue?.revenue || 0,
      subtitle: revenueSubtitle,
      subtitleColor: colors.textSecondary,
    },
    {
      icon: <PercentIcon sx={{ fontSize: 18, color: colors.primary }} />,
      iconBg: colors.primaryAlpha("15"),
      title: "HIGHEST CONVERSION",
      value: `${(data?.highestConversion?.rate || 0).toFixed(1)}%`,
      subtitle: data?.highestConversion?.name
        ? `${data.highestConversion.name}${data.highestConversion.totalLeads ? ` · ${data.highestConversion.totalLeads} leads` : ""}`
        : "No data",
      subtitleColor: colors.success,
      trend: true,
    },
    {
      icon: <AttachMoneyIcon sx={{ fontSize: 18, color: "#10B981" }} />,
      iconBg: "#D1FAE5",
      title: "AVG. SERVICE VALUE",
      value: data?.avgServiceValue
        ? data.avgServiceValue
        : `${winRatio}%`,
      subtitle: data?.avgServiceValue
        ? "Per lead average"
        : `${totalWon} won of ${totalLeads} leads`,
      subtitleColor: colors.textSecondary,
    },
    {
      icon: (
        <AccountBalanceWalletOutlinedIcon
          sx={{ fontSize: 18, color: colors.success }}
        />
      ),
      iconBg: colors.successLight,
      title: "TOTAL SERVICE REVENUE",
      value: data?.totalRevenue || 0,
      subtitle: `Conversion: ${(data?.conversionRate || 0).toFixed(1)}%`,
      subtitleColor: colors.success,
      trend: true,
      trendColor: colors.success,
    },
  ];

  return (
    <Grid container spacing={2.5} sx={{ alignItems: "stretch" }}>
      {metrics.map((metric, index) => (
        <Grid
          key={index}
          size={{ xs: 12, sm: 6, md: 3 }}
          sx={{ display: "flex" }}
        >
          <MetricCard {...metric} />
        </Grid>
      ))}
    </Grid>
  );
};

export default ServiceMetricsCards;
